'use client';

import type { ReactNode } from 'react';

/** Accent of the overline and, by convention, of whatever the reward row highlights. */
export type NotificationToastAccent = 'amber' | 'blue';

const ACCENT_TEXT: Record<NotificationToastAccent, string> = {
  amber: 'text-amber-400',
  blue: 'text-blue-400',
};

interface Props {
  /** Fills the 56×56 glass tile on the left — art, an icon, whatever the event calls for. */
  icon: ReactNode;
  /** Small uppercase line above the title: what just happened. */
  overline: string;
  /** Headline of the event. A node, so composed titles (text + inline coin amount) fit. */
  title: ReactNode;
  /** Optional line under the title: what the user got. */
  reward?: ReactNode;
  accent?: NotificationToastAccent;
  /** When set, the whole toast is a button. */
  onClick?: () => void;
}

/**
 * In-app notification card — the shared surface behind every live toast (achievement unlock,
 * SHOT BAND quest completion...). Rendered inside a sonner `toast.custom(...)`. iOS-26
 * liquid-glass (same recipe as the tab bar), pop-in + one-shot gloss sweep on mount.
 */
export function NotificationToast({ icon, overline, title, reward, accent = 'amber', onClick }: Props) {
  const content = (
    <>
      <div className="relative z-10 h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-white/5">{icon}</div>

      <div className="relative z-10 min-w-0 flex-1">
        <div className={`text-[11px] font-semibold uppercase tracking-wide ${ACCENT_TEXT[accent]}`}>{overline}</div>
        <div className="truncate text-sm font-bold text-label-primary">{title}</div>
        {reward && <div className="mt-1 flex items-center gap-2">{reward}</div>}
      </div>
    </>
  );

  // One-shot gloss streak that sweeps across the glass on mount.
  const shine = (
    <span className="animate-shot-ach-shine pointer-events-none absolute inset-y-0 left-0 z-20 w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
  );

  // mx-auto: on mobile sonner stretches the toast <li> full-width, so a fixed-width card
  // pins to the left edge — auto side-margins re-centre it under the top-center toaster.
  const base =
    'animate-shot-ach-toast-in liquid-glass relative mx-auto flex w-[min(360px,calc(100vw-32px))] items-center gap-3 overflow-hidden rounded-2xl p-3 text-left backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125';

  if (!onClick) {
    return (
      <div className={base}>
        {content}
        {shine}
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${base} cursor-pointer transition active:scale-[0.99] active:opacity-90`}>
      {content}
      {shine}
    </button>
  );
}
